import React from 'react'
import { IconAttendance, IconCalendarCheck, IconClockSolid, IconSchool, IconUser, IconSurvey } from '../icons/library'

export type AvatarInfo = { color: string; initials?: string; img?: string; mini?: AvatarInfo; self?: boolean }

interface AvatarProps {
  /** The avatar information, including color, initials, image URL, and optional mini avatar. */
  src: AvatarInfo
  /** The size of the avatar. */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  /** Additional classes for styling. */
  className?: string
}

const sizes = {
  xs: 'h-[14.1px] w-[14.1px] min-w-[14.1px]',
  sm: 'h-[25px] w-[25px] min-w-[25px]',
  md: 'h-[35.25px] w-[35.25px] min-w-[35.25px]',
  lg: 'h-[50px] w-[50px] min-w-[50px]',
  xl: 'h-[70px] w-[70px] min-w-[70px]',
}

const textSizes = {
  xs: 'text-[8px]',
  sm: 'text-sm',
  md: 'text-lg',
  lg: 'text-xl',
  xl: 'text-2xl',
}

const imgIconOverrides: { [key: string]: JSX.Element } = {
  'icon-calendar': <IconAttendance className="text-white" size="1x" />,
  'icon-user': <IconUser className="text-white" size="lg" />,
  'icon-school': <IconSchool className="text-white" />,
  'icon-calendar-check': <IconCalendarCheck className="text-white" />,
  'icon-clock': <IconClockSolid className="text-white" />,
  'icon-assessment': <IconSchool className="text-white" />,
  'icon-survey': <IconSurvey className="text-white" />,
}

/**
 * Avatar component that displays a circular avatar with initials or an image.
 */
export const Avatar: React.FC<AvatarProps> = React.memo(({ className, src, size = 'md' }) => {
  const [useImg, setUseImg] = React.useState(!!src.img)
  const ring = src.self ? 'ring ring-blue-200 ' : ''
  const icon = src.img && imgIconOverrides.hasOwnProperty(src.img) ? imgIconOverrides[src.img] : null
  let textSize = textSizes[size]

  if (textSize === 'text-lg') {
    if (src.initials?.length === 3) textSize = 'text-base'
    else if (src.initials?.length === 4) textSize = 'text-sm'
    else if (src.initials?.length === 5) textSize = 'text-[.65rem]'
  }
  return (
    <div
      className={`relative flex items-center justify-center rounded-full text-white ${sizes[size]} ${ring} ${className}`}
    >
      {useImg && !icon ? (
        <img src={src.img} alt="Avatar" className="h-full w-full rounded-full" onError={() => setUseImg(false)} />
      ) : (
        <div
          className={`flex h-full w-full items-center justify-center rounded-full ${textSize} font-bold`}
          style={{ backgroundColor: src.color }}
        >
          {icon || src.initials || ''}
        </div>
      )}
      {src.mini && <Avatar src={src.mini} size="xs" className="!absolute right-0 top-0" />}
    </div>
  )
})

interface AvatarButtonProps extends AvatarProps {
  className?: string
}

/**
 * AvatarButton component that displays a button containing an avatar.
 */
export const AvatarButton = React.forwardRef<HTMLButtonElement, AvatarButtonProps>(
  ({ className, src, size, ...props }, ref) => {
    return (
      <button className={`flex focus:outline-none ${className || ''}`} ref={ref} {...props}>
        <Avatar src={src} size={size} />
      </button>
    )
  }
)
